import React from 'react';
import type { FormatXMLElementFn } from 'intl-messageformat';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Anchor } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ApplicationEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import messages from './messages.tsx';

const CUSTOMER_AGREEMENT_URL = 'https://www.atlassian.com/legal/atlassian-customer-agreement';
const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy';
const JPD_CURRENT_PRICING_URL = 'https://www.atlassian.com/software/jira/product-discovery/pricing';

type LegalNoticeProps = {
	edition: ApplicationEdition | null;
	hasPaymentDetails: boolean;
};
export const useLegalNotice = ({ edition, hasPaymentDetails }: LegalNoticeProps) => {
	const { formatMessage } = useIntl();

	const legalNoticeOverrides = {
		tc: (...args) => {
			const onClick: (
				e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
				analyticsEvent: UIAnalyticsEvent,
			) => void = (e, analyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'customerAgreementUrl');
			};

			return (
				<Anchor
					href={CUSTOMER_AGREEMENT_URL}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onClick}
				>
					{args}
				</Anchor>
			);
		},
		pp: (...args) => {
			const onClick: (
				e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
				analyticsEvent: UIAnalyticsEvent,
			) => void = (e, analyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'privacyPolicyUrl');
			};

			return (
				<Anchor
					href={PRIVACY_POLICY_URL}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onClick}
				>
					{args}
				</Anchor>
			);
		},
		cp: (...args) => {
			const onClick: (
				e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
				analyticsEvent: UIAnalyticsEvent,
			) => void = (e, analyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'currentPricingUrl');
			};

			return (
				<Anchor
					href={JPD_CURRENT_PRICING_URL}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onClick}
				>
					{args}
				</Anchor>
			);
		},
	} satisfies Record<string, FormatXMLElementFn<React.ReactNode>>;

	const hasPaymentDetailsLegalText = hasPaymentDetails
		? formatMessage(messages.hasPaymentDetailsLegalText, legalNoticeOverrides)
		: '';

	const hasPaymentDetailsLegalTextForConsent = hasPaymentDetails
		? formatMessage(messages.hasPaymentDetailsLegalText, {}, { ignoreTag: true })
				.toLocaleString()
				.replaceAll(
					/<(cp)>/g,
					`<a href="${JPD_CURRENT_PRICING_URL}" target="_blank" rel="noopener noreferrer">`,
				)
				.replaceAll(/<\/(cp)>/g, '</a>')
		: '';
	const trialInfo =
		edition === 'STANDARD_EDITION'
			? formatMessage(messages.standardTrialInfo, legalNoticeOverrides)
			: formatMessage(messages.trialInfo, legalNoticeOverrides);
	const trialInfoPlain =
		edition === 'STANDARD_EDITION'
			? formatMessage(messages.standardTrialInfo, {}, { ignoreTag: true })
			: formatMessage(messages.trialInfo, {}, { ignoreTag: true });

	const trialInfoForConsent = trialInfo
		? trialInfoPlain
				.toLocaleString()
				.replaceAll(
					/<(tc)>/g,
					`<a href="${CUSTOMER_AGREEMENT_URL}" target="_blank" rel="noopener noreferrer">`,
				)
				.replaceAll(
					/<(pp)>/g,
					`<a href="${PRIVACY_POLICY_URL}" target="_blank" rel="noopener noreferrer">`,
				)
				.replaceAll(/<\/(tc|pp)>/g, '</a>')
		: '';
	if (edition === 'STANDARD_EDITION' || edition === 'PREMIUM_EDITION') {
		return {
			trialInfo,
			hasPaymentDetailsLegalText,
			tosPrivacyTextForConsent: `${trialInfoForConsent} ${hasPaymentDetailsLegalTextForConsent}`,
		};
	}

	return null;
};
